/**
 * AiReviewPage — `/research/ai-review` (v38 requirements-analysis
 * `#page-ai-review`). Owns ALL screen state and drives the R1/R2/R3 →
 * async-run state machine from the sub-task 04 PRD's Mermaid diagram
 * (Empty → Configuring → Running → Results → Stale/Failed); composes
 * `CriteriaPanel`, `useCreateAiReviewRun`/`useAiReviewRun` and `ScoresTable`
 * without modifying any of them.
 *
 * The import under review is NOT page state: it is the app-wide selection
 * owned by `SelectedImportProvider` and rendered by `<ImportSelectionBar>`,
 * shared with `/research/backchannel-tree`. This page only reacts to it
 * (clearing a run when it changes) and vetoes a change that would silently
 * discard an unapplied full run.
 *
 * Single column (POC cut — no Library panel): see the grid-seam comment
 * below and the PRD's "Library seam decision".
 *
 * "Review candidates" produces a NEW import and THIS PAGE LEAVES FOR IT AT ONCE
 * (Trello #711 cl.5, revised 2026-08-26). `POST /ai-reviews` writes the derived
 * `imports` row in the same transaction as the run and now names it on the 202,
 * so there has never been anything to wait for. `onSuccess` navigates to
 * `/research/imports/<output_import_id>?run=<job_id>` and the DESTINATION polls
 * the run — it renders the scoring progress, the failure reason, and the
 * criteria card once the run lands.
 *
 * An earlier revision held this page open until `applied` before navigating.
 * That is what the stall machinery below was built for, and it is why `?run=`
 * matters: without the run id the destination cannot ask about its own run at
 * all (`by-import` requires `applied`).
 *
 * The browser has no part in the WORK: the scoring worker claims the apply hop
 * itself. There is no auto-apply bridge and no apply mutation, because both
 * existed to make this page DRIVE the second hop.
 *
 * A stall deadline IS still here (`useAiReviewRun`) and now governs the PREVIEW
 * path only — the one run that still finishes on this page. It is not the one
 * #726 removed: that offered a browser-driven retry, this one stops waiting and
 * says where to look. A run can sit non-terminal forever — a failed apply
 * hand-off parks it back on `succeeded` for operator recovery, and the sweeper
 * settles the derived FILE, not the run.
 *
 * There is no `beforeunload` guard, deliberately. Nothing here is load-bearing
 * for the file: close the tab and the work continues, the file lands on the
 * imports list, and a build that strands is settled `failed` THERE by the
 * sweeper — on the file, which is why the abandon banner points at it.
 *
 * Nothing is written to the import that was REVIEWED, so its
 * `import-contacts` query is deliberately not invalidated (R2.5).
 *
 * What is left on this page is the preview: a sample run, scored in place,
 * which never produces a file and therefore never leaves.
 */
import type { ImportSummary } from "@nu-assist/contracts";
import { useMe } from "@nu-assist/shared-fe";
import { PageHeading } from "@nu-assist/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { type AiReviewClient, createAiReviewClient } from "../../../clients/aiReview.client";
import { type ImportsClient, createImportsClient } from "../../../clients/imports.client";
import { type PeopleClient, defaultPeopleClient } from "../../../clients/people.client";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { activeFlags } from "../../../config/featureFlags";
import { usePreviewModeIfMounted } from "../../../features/PreviewProvider";
import {
  DEFAULT_DEEP_LINK_PARAMS,
  ImportSelectionBar,
} from "../../../features/imports/selection/ImportSelectionBar";
import { useSelectedImport } from "../../../features/imports/selection/SelectedImportContext";
import { aiReviewUnusable } from "../../../features/imports/selection/requirements";
import { PersonPanel } from "../../../features/person-panel/PersonPanel";
import { PersonView } from "../../../features/person-panel/PersonView";
import { personShareUrl } from "../../../features/person-panel/sharePreview";
import { useListPersonPanel } from "../../../features/person-panel/useListPersonPanel";
import { pageVisible } from "../../../features/useFeatureAccess";
import { personPagePath } from "../../../routing/paths";
import { displayTitle } from "../import-detail/importTitle";
import { useResolveAvatars } from "../import-detail/useResolveAvatars";
import { CriteriaPanel, type Criterion } from "./CriteriaPanel";
import { InlineCriteriaSuggestions } from "./InlineCriteriaSuggestions";
import { OutputFileNameField } from "./OutputFileNameField";
import { ScoresTable, type ScoresTableSort } from "./ScoresTable";
import { mergeCarriedCriteria } from "./carriedCriteria";
import { emptyCriteria, nextCriterionId, reconcileCriteria } from "./criteriaRows";
import { criterionOrigin } from "./criteriaSuggestions";
import { isDuplicateFilename, suggestOutputFilename } from "./outputFilename";
import {
  criteriaToAnnexColumns,
  joinScores,
  rationaleForRun,
  scoreOnlyRows,
  scoredContactCount,
  scoredContactIds,
} from "./previewRows";
import { useAiReviewRun, useCreateAiReviewRun, useLatestAiReview } from "./useAiReviewRun";
import { useOutputFilenameSuggestion } from "./useOutputFilenameSuggestion";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

/** Mirrors `CriterionRow`'s own input cap and the backend's field limit —
 *  enforced again here since request-building is a second, independent
 *  boundary (defense in depth, not a UI concern). */
const MAX_CRITERION_LENGTH = 100;

/** Preview-sample size: score this many random contacts so the recruiter can
 *  see representative AI output before committing to the full run's cost. */
const SAMPLE_SIZE = 5;

/** Max criteria per run — mirrors the backend's `AiReviewCreateRequest.criteria`
 *  cap (`max_length=8`). Keeps the prompt's output bounded (~1 rationale per
 *  criterion) and coverage tractable; the FE disables "Add criterion" at this
 *  count so a run never 422s on too-many-criteria. */
const MAX_CRITERIA = 8;

/** What "these criteria are pinned" carries: which review they came from (the
 *  banner resolves its name from that id) and how many arrivals the cap refused
 *  (the truncation line).
 *
 *  There is exactly one way to pin — arriving on `?criteriaFrom=` — so the
 *  source is always a file, and always that one. An earlier revision also
 *  pinned from a button on this page, which is why this used to carry a
 *  nullable captured NAME as well. */
interface PinnedCriteria {
  sourceImportId: string;
  dropped: number;
}

// One client per app session (lazy-resolves `globalThis.fetch` at call
// time, so MSW interception still applies) — same rationale as
// ImportsPage.tsx's `defaultClient`.
const defaultAiReviewClient = createAiReviewClient({ apiBase: API_BASE });
const defaultImportsClient = createImportsClient({ apiBase: API_BASE });

export interface AiReviewPageProps {
  /** Injectable for tests (defaults to the module-shared typed clients). */
  aiReviewClient?: AiReviewClient;
  importsClient?: ImportsClient;
  peopleClient?: PeopleClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
}

export function AiReviewPage({
  aiReviewClient,
  importsClient,
  peopleClient,
  meEmailOverride,
}: AiReviewPageProps = {}) {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");
  const activeAiReviewClient = aiReviewClient ?? defaultAiReviewClient;
  const activeImportsClient = importsClient ?? defaultImportsClient;

  // The import under review is the app-wide selection (shared with the
  // Backchannel Tree page) — `<ImportSelectionBar>` owns picking it, the
  // R1 deep-link (`?import=<id>`) included.
  const { selected } = useSelectedImport();
  const selectedImportId = selected?.importId ?? null;

  // `?criteriaFrom=<id>` — "score a list with THAT review's criteria", sent by
  // the detail page's `Edit criteria and rerun`, which is the ONLY door and
  // always sends it alongside `?import=` at the same id. (It used to name
  // `Run on another list…`, a second button deleted in #896.) Orthogonal
  // to `?import=`, which names the list to score: the two can sit in one URL
  // and mean different things.
  //
  // READ-ONLY, and that is a boundary rather than a convenience.
  // `ImportSelectionBar` owns this screen's query string (its docblock says so
  // in capitals); a second writer is how the collapse and the reconcile ended
  // up fighting over one `setSearchParams` before. The param therefore SURVIVES
  // a selection change — the bar's `new URLSearchParams(prev)` copies it
  // through untouched — so a reload re-carries the same criteria. That is
  // coherent: the URL says where they came from, and the pin below is one-shot
  // per id, so a reload re-pins rather than re-appending.
  const [searchParams] = useSearchParams();
  const criteriaFromId = searchParams.get("criteriaFrom")?.trim() || null;
  // BOTH spellings, via the bar's own constant. It honours `import` AND the
  // historical `importId`, so reading only the first here would open the finder
  // on an arrival the bar had already resolved — and the gate's whole question
  // is "does the URL already name a list?".
  //
  // READ FROM THE URL, NOT FROM `selectedImportId`. The selection comes from
  // `useSelectedImport()` and can be restored from localStorage, so keying the
  // finder on it would suppress the finder on a genuine carry-only arrival —
  // a URL naming criteria but no list — for anyone who happens to have a
  // stored selection, silently breaking the one flow that rule exists to
  // serve. The URL says what this
  // navigation asked for; the provider says what the browser remembered.
  const importParamId =
    DEFAULT_DEEP_LINK_PARAMS.map((k) => searchParams.get(k)?.trim()).find(Boolean) ?? null;

  const [sort, setSort] = useState<ScoresTableSort | null>(null);

  const [criteria, setCriteria] = useState<Criterion[]>(emptyCriteria);
  /** Rows added from "Suggested by AI from Thrive", by criterion id, with
   *  the text as suggested: the run says which criteria came from AI and
   *  whether they were edited, and deleting one is reported (2026-11-03,
   *  measuring how the suggestions are used). Every path that rewrites the
   *  rows without an edit (a carry, a prefill, a selection's reset) clears
   *  it: those keep row ids by position, so a stale entry would record a
   *  file's criterion as an edited suggestion (PR #1118 iter 6, A6-2/C6-5).
   *  A carried AI criterion therefore counts as manual. */
  const aiCriteriaRef = useRef(new Map<string, string>());
  /** The criteria in the form are PINNED: they belong to the session, not to
   *  the selected list. Render state only — the rule it stands for is enforced
   *  by `criteriaAreSessionOwned` (the pending window and the pinned form) and
   *  by `formHoldsOwnWorkRef` (anything the recruiter typed), further down. */
  const [pinned, setPinned] = useState<PinnedCriteria | null>(null);
  /** We arrived on `?criteriaFrom=` and have not settled it yet.
   *
   *  Separate from `pinned` because the two cover different halves of the same
   *  window: this one is true from the FIRST RENDER (the param is known
   *  synchronously), `pinned` only once the criteria have actually landed. The
   *  gap between them is a network round trip, and it is exactly when the
   *  selection's own prefill would otherwise slip in. */
  const [carryPending, setCarryPending] = useState(() => criteriaFromId !== null);
  // R1: the name the run's derived import will carry. Seeded from the
  // suggestion and fully owned by the recruiter thereafter — `…Edited` records
  // that they touched it, so a probe result landing late never clobbers a
  // hand-written name.
  const [outputFilename, setOutputFilename] = useState("");
  const [outputFilenameEdited, setOutputFilenameEdited] = useState(false);
  // Trimmed ONCE and reused at both send sites (create, and the recovery
  // apply). Two independent `.trim()` calls is how one of them was left
  // un-trimmed before.
  const trimmedOutputFilename = outputFilename.trim();
  const [reviewId, setReviewId] = useState<string | null>(null);
  /** The import `reviewId` was started for.
   *
   *  `reviewId` alone is not enough to know whether a run describes what is on
   *  screen. The selection effect clears it, but an effect runs AFTER the
   *  render that changed `selected` — so for one render the run is the old
   *  import's while `selectedImportId` is already the new one. That render was
   *  enough to fire `GET /imports/<new>/contacts?ids=<old ids>` (a guaranteed
   *  empty response) and to paint "The scored candidates are no longer in this
   *  people list" for a frame. Comparing this against the live selection makes
   *  the mismatch visible DURING that render instead of one effect later.
   *
   *  NOT COVERED BY A TEST, deliberately, and said out loud rather than left to
   *  be discovered. I wrote three versions of one and all three passed with this
   *  guard reverted: the window is a single render, and by the time RTL can
   *  assert, the selection effect has already cleared `reviewId`, so the
   *  vanished branch is never reached either way. Rather than keep a test that
   *  cannot fail — the defect class this PR has been caught on repeatedly — the
   *  guard rests on the trace above and on the reviewer's, and this note marks
   *  it as unpinned so nobody mistakes the absence for coverage. */
  const [runImportId, setRunImportId] = useState<string | null>(null);
  /** Was the run we most recently STARTED a preview? Read only when the poll
   *  never produced data — see `abandonedIsPreview`. */
  const [lastRunWasPreview, setLastRunWasPreview] = useState(false);
  const [stale, setStale] = useState(false);
  const [createError, setCreateError] = useState(false);
  // "Review candidates" is confirm -> create -> watch -> open the file. It used
  // to be a compound action this page had to DRIVE: run the FULL review here,
  // wait for `succeeded`, then fire `POST /apply` from the browser. The scoring
  // worker claims that hop itself now, so what is left here is watching, not
  // bridging.
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);

  const createRun = useCreateAiReviewRun({ client: activeAiReviewClient });
  /** A preview's create is in flight (it may be scored inline, see `ScoresTable`'s `previewPending`). */
  const previewPosting = createRun.isPending && createRun.variables?.sample_size != null;
  const {
    run: runForReviewId,
    isPolling,
    stalled: runStalled,
    pollFailed,
  } = useAiReviewRun({
    client: activeAiReviewClient,
    reviewId,
    sortKey: sort?.key,
    sortDir: sort?.dir,
  });
  // A run belongs to the page only while its import is still the selected one.
  // Everything downstream — `hasResults`, the contacts query, the body branch —
  // reads this, so the stale-render window closes in one place rather than in
  // each consumer.
  const run = runImportId === selectedImportId ? runForReviewId : undefined;

  // ---- The preview table's rows -------------------------------------------
  //
  // A run result is a SCORE MATRIX: `import_contact_id`, a display name, and
  // the per-criterion cells. It carries no avatar, no headline and no current
  // position — so it cannot fill the table the Import Detail page renders, and
  // Trello #729 asks for exactly that table. The profile half comes from the
  // contacts endpoint, fetched by the ids the run actually scored.
  //
  // ONE REQUEST, via the `ids` filter. Without it this would mean paging the
  // whole parent import (`page_size` caps at 100) to find five rows — 30
  // requests on a 3,000-contact list, to render a five-row sample.
  const previewIds = useMemo(() => scoredContactIds(run), [run]);
  const previewContactsQuery = useQuery({
    // Keyed on the RUN, not just the ids: re-previewing with new criteria mints
    // a new run over a new sample, and a key that ignored it would serve the
    // previous sample's rows from cache.
    queryKey: ["ai-review-preview-contacts", selectedImportId, run?.job_id, previewIds],
    queryFn: ({ signal }) =>
      activeImportsClient.listContacts(
        selectedImportId as string,
        { ids: previewIds, page_size: previewIds.length },
        { signal }
      ),
    // NOT for an applied run. `rekey_scores` has moved the ids onto the child
    // by then, so this fetch is guaranteed to return zero rows — firing it
    // spends a request (up to 25 ids) on every mount purely to discard it,
    // and its pending/error states describe causes that cannot apply.
    enabled: selectedImportId != null && previewIds.length > 0 && run?.status !== "applied",
  });
  // The join lives in `previewRows` (pure, unit-tested). Rows come back in the
  // RUN's order — the endpoint sorts by name, and letting that win would
  // reshuffle the preview under a sort nobody asked for.
  const previewRows = useMemo(() => {
    if (!run) return [];
    // An applied run has no fetch to join against — see `scoreOnlyRows`. It
    // renders from the run itself, which is what `dev` did for this state.
    if (run.status === "applied") return scoreOnlyRows(run);
    return joinScores(previewContactsQuery.data?.items ?? [], run);
  }, [previewContactsQuery.data, run]);
  const previewColumns = useMemo(() => (run ? criteriaToAnnexColumns(run) : []), [run]);
  const previewRationaleFor = useMemo(() => rationaleForRun(run), [run]);
  // Same hook the contacts table uses on the detail page, so a preview row's
  // avatar resolves by the same durable-S3-key path and never falls back to the
  // expiring licdn URL.
  const previewAvatars = useResolveAvatars(activeImportsClient, previewRows);

  // Person side panel over the scored rows — the SAME gate and list logic as
  // Import Detail (`pageVisible(…, "person-panel")`, `useListPersonPanel`), but
  // an avatar opens it on the AI Review tab. `usePersonPanelUrl` writes only
  // `person` and `tab`, so `?import=` and the rest of the query survive.
  // "Suggested by AI from Thrive" (epic
  // 2026-10-30-ai-review-criteria-suggestions): the suggestions under the
  // criteria, asked as soon as a list is selected. Behind
  // `?preview=suggest-inline` until released; the client chose this UI over
  // the button + dialog on 2026-11-03.
  const inlineSuggestEnabled = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "linkedin",
    "ai-review-suggest-inline"
  );
  const personPanelEnabled = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "linkedin",
    "person-panel"
  );
  const {
    personUrl,
    openHandle,
    personStops,
    openIndex,
    openRow,
    openRowId,
    openPerson,
    closePerson,
    stepPerson,
  } = useListPersonPanel({
    rows: previewRows,
    enabled: personPanelEnabled,
    openTab: "ai-review",
  });
  const peopleApi = useMemo(() => peopleClient ?? defaultPeopleClient(), [peopleClient]);
  const location = useLocation();
  const panelListTitle = displayTitle(selected?.filename) || "this list";
  // The panel's AI Review tab has its prompt viewer up: the panel is
  // suspended behind it, so its keys act on the viewer.
  const [panelViewerOpen, setPanelViewerOpen] = useState(false);

  // We stopped waiting and the run did not finish — the recruiter is owed a way
  // out. `applied` is excluded because a run that reached it has nothing left
  // to report here; `failed` is excluded because `ScoresTable` owns that
  // message and two banners saying different things is worse than either.
  // (The `applied` clause used to cite "the redirect is already taking them to
  // the file". That redirect was deleted when the navigation moved to the 202.)
  // NO `!isPolling` term, and that omission is the whole point.
  //
  // It was there when the deadline STOPPED the poll, and it survived iter 3's
  // change to a back-off — where `isPolling` deliberately stays true past the
  // deadline because the page is still watching. `stalled` implies
  // `isRunInFlight`, which implies `isPolling`, so `!isPolling` silently made
  // the `runStalled` term dead: the 15-minute deadline became a user-visible
  // no-op and the banner degraded to a poll-failure banner
  // (`/pr-review` iter 4 — Agent A blocker, Agent C N1, found independently).
  //
  // The lesson, stated where the next person will change this: `isPolling`
  // changed MEANING in iter 3, and this was its one remaining old-sense
  // consumer. Exporting one predicate removed the liveness divergence; it could
  // not remove a consumer reading the right value with the wrong intent.
  //
  // The two status exclusions are DEFENSIVE AND UNREACHABLE, and saying so is
  // the point — a previous revision shipped a test for the `failed` one that
  // passed with the term deleted, and a commit message that called it guarded
  // (`/pr-review` iter 4, Agent B N6). No test can reach either:
  //
  //   stalled     => isRunInFlight(run)  => run is not `failed` and not `applied`
  //   pollFailed  => the query errored, and both terminal statuses have already
  //                  stopped the poll, so no later poll can error into them
  //
  // They stay because they cost nothing and state the intent at the point of
  // use: `applied` belongs to the redirect, `failed` belongs to ScoresTable's
  // banner. They are not claimed as covered.
  const abandoned =
    (runStalled || pollFailed) && run?.status !== "applied" && run?.status !== "failed";
  // A PREVIEW produces no file, so every sentence about "your people list"
  // would be false for one (`/pr-review` iter 2, Agent A blocker 2 — the first
  // page test pinned the full-run copy while driving a preview).
  //
  // The run is the authority WHEN THERE IS ONE. There is not always: a poll
  // that fails from its first attempt leaves `run` undefined, and that is
  // precisely a case this banner has to render. What the page always knows is
  // what it ASKED FOR, which is what `lastRunWasPreview` records.
  //
  // BOTH arms, and the full-run one is DEFENSIVE — not, as this comment claimed
  // for one round, reachable through the legacy F6 path.
  //
  // That claim was wrong twice over (`/pr-review` round 8 adjudication). F6 is
  // defined on `AiReviewResult.output_import_id` — a legacy run reaching
  // `applied` with no file — from the era when navigation fired at `applied`.
  // Transplanting the term onto the 202's field ignores that the two fields
  // have deliberately opposite guarantees, which this PR's own schema prose
  // insists on at length. And the server cannot emit a full-run 202 with a null
  // id: `create` mints `child_id` whenever `builds_a_file`, and the
  // double-click path returns the winner's.
  //
  // The "20 tests broke when I deleted it" offered as proof was an artifact of
  // a broken edit — a ReferenceError from removing the backing state too — not
  // evidence of reachability. The real figure is 2, and both reach the arm only
  // through hand-rolled fakes that omit the optional field; the file's own MSW
  // handler models the server correctly and does not.
  //
  // It stays anyway, for the reason that actually holds: `output_import_id` is
  // OPTIONAL on the wire, so a client tolerating null is correct, and a rolling
  // deploy of this bundle against an API predating the field is a real if
  // transient window. Defensive against deploy skew — not a live product path.
  const abandonedIsPreview = run ? run.sample_size != null : lastRunWasPreview;

  // R1.4: `<N>` comes from the existing `GET /imports` — no new endpoint. The
  // probe is advisory end to end (F2: a 5xx degrades to the `v1` fallback and
  // is never surfaced).
  const { suggestion, existingFilenames, truncated } = useOutputFilenameSuggestion({
    client: activeImportsClient,
    parentFilename: selected?.filename ?? null,
    // No `parentImportId`: the version max is scoped by NAME, not by lineage.
    // ❓Q1 scoped it by lineage, which was right while only an original could
    // be reviewed; a chained review joins its parent's name series while
    // hanging off a different parent, so a lineage-scoped max cannot see it and
    // re-mints a name that already exists.
  });

  // The output name used to LOCK once a full run owned it, so a rename could
  // not be silently dropped between the create and the apply. There is no gap
  // to protect any more: the name rides the create request that writes the row,
  // so it is already persisted server-side before this page does anything else.
  // A preview never owned a name.

  // Re-seed from the probe once it settles for the CURRENT import (the hook
  // returns null while the previous import's page is still on screen).
  // The selection effect has already put the optimistic `v1` in place, so this
  // only ever corrects the version upward — no empty flash, no race with a
  // fast click on Preview.
  //
  useEffect(() => {
    if (suggestion === null || outputFilenameEdited) return;
    setOutputFilename(suggestion);
  }, [suggestion, outputFilenameEdited]);

  // R1.5 + R1.9: advisory duplicate warning, suppressed when the probe page
  // was truncated — a "no duplicate" verdict computed from a partial list is a
  // false negative, and silence is more honest than a wrong reassurance.
  const outputFilenameDuplicate =
    !truncated && isDuplicateFilename(outputFilename, existingFilenames);

  // There is no applied-redirect effect on this page. It is worth saying so
  // HERE because this spot has now carried three different claims about one:
  // "it does not exist and the page leaves on the 202" (true, then not), "it is
  // BELOW, search `run?.status !== \"applied\"`" (true for one iteration, and
  // that search string now lands on an unrelated `abandoned` expression), and
  // this. The navigation happens in the create `onSuccess` below, on the 202.
  //
  // Three rewrites of one comment in one branch is the actual lesson, more than
  // any of the three versions: a comment that points at OTHER code by position
  // or by search string rots every time that code moves. This one names a fact
  // about this page instead (`/pr-review` iter 6, Agent C M4).

  // A new import invalidates any prior run outright — its results belong to
  // the OLD import's candidates, so clear rather than merely mark stale (stale
  // still implies "the same run, just needs a refresh"). This runs on EVERY
  // change of the shared selection, including one made on another page or
  // restored from a previous session, not just on a click in this page's bar.
  //
  // R1.7 rides here rather than in a click handler for exactly that reason:
  // the selection is now app-wide, so a name seeded only on THIS page's click
  // would go stale the moment the import changed anywhere else.
  const lastImportIdRef = useRef<string | null>(selectedImportId);
  // Keyed on the import CHANGE alone. `selected` is read for its filename at
  // that instant; adding it to the deps would re-run this on every unrelated
  // field the shared selection refreshes (status polling rewrites it), wiping
  // a name the recruiter typed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (lastImportIdRef.current === selectedImportId) return;
    lastImportIdRef.current = selectedImportId;
    // A new import gets that import's suggestion, discarding any manual edit.
    // Seeded synchronously with the `v1` shape so the field is never
    // momentarily empty (an empty name gates Apply); the probe effect corrects
    // the version as soon as it answers.
    if (selected?.filename) {
      // `suggestion ?? optimistic`, and the `??` is the fix for a real bug
      // (`/pr-review` iter 6 — disclosed by me; Agent A 3.4 and Agent C both
      // said close it here rather than ship the disclosure).
      //
      // The probe's query key is `reviewProbeQuery(parentFilename)`, which runs
      // the name through `seriesStemOf` — so a review file and the RAW PARENT
      // it derives from produce the identical key. Switching between them
      // leaves the probe already settled and `suggestion` unchanged, so the
      // re-seed effect below never fires again (its deps compare equal) and
      // whatever this line writes is final. Writing the optimistic `v1`
      // unconditionally meant the field ended up naming a file that already
      // existed, with the duplicate warning under it.
      //
      // Adding `selectedImportId` to the re-seed effect's deps does NOT fix it:
      // that effect is declared ABOVE this one, so in the same commit it runs
      // first and this line overwrites its answer. Ordering, not staleness.
      //
      // The optimistic fallback still matters for a switch to a DIFFERENT stem,
      // where the key changes, the probe goes back to placeholder and
      // `suggestion` is null — the field must never be momentarily empty,
      // because an empty name gates Apply.
      setOutputFilename(suggestion ?? suggestOutputFilename(selected.filename, []));
      setOutputFilenameEdited(false);
    }
    setReviewId(null);
    setRunImportId(null);
    setSort(null);
    setStale(false);
    setCreateError(false);
    // Criteria are cleared HERE only when they arrived by PREFILL and the
    // recruiter never touched them — they belong to the file they came from,
    // so leaving that file takes them with it. That covers the switch to a raw
    // import and to no selection at all, neither of which the seeding effect
    // below can reach (it returns immediately when the selection is not a
    // review file), which is why nothing used to clear them and the previous
    // file's criteria sat in the form pretending to be this one's.
    //
    // Criteria the recruiter TYPED are deliberately left alone, which is the
    // original reason this effect touched none of them: switching between two
    // RAW imports must keep what was typed, since nothing would refill it. An
    // edit made on top of a prefill counts as typed — `handleCriteriaChange`
    // drops the provenance the moment they touch a row.
    //
    // PINNED criteria never reach this clause, and the mechanism is NOT a
    // provenance transfer — an earlier revision of this comment named a
    // `pinCriteria` helper that no longer exists (PR #869, Agent A: five
    // identifiers in this file named code that had been deleted).
    //
    // What actually keeps them out: the carry writes the criteria directly and
    // never sets `criteriaSeededFromRef`, so the condition above is false for a
    // carried form. Changing the list is the ENTIRE POINT of pinning, so a
    // clear here would delete the criteria at the exact moment they are wanted.
    //
    // A guard on the pinned state was written here first and then removed: it
    // made both mechanisms individually unnecessary, so every mutation of
    // either one still passed the whole suite. Two guards that mask each other
    // are not defence in depth, they are two untested guards (verified by
    // mutation, 2026-09-10).
    if (criteriaSeededFromRef.current !== null && selected?.import_format !== "ai_review") {
      // Back to ONE blank row — written into the rows already there, so the
      // first keeps its node (PRD 2026-10-06, C1) and the rest are dropped.
      //
      // NOT on the way to ANOTHER review file: its prefill is about to be
      // reconciled into these rows by position, so they keep the previous
      // file's values until it lands (blanking them showed N empty rows for
      // the whole round trip, PR #1016 iter 2, A §3.2 / C M-D) and every node
      // survives the switch. `criteriaSeededFromRef` keeps naming the PREVIOUS
      // file meanwhile, which is what holds Preview (`rowsHoldAnotherFilesPrefill`
      // below) and what the seeding effect collapses to one blank row when this
      // file has no criteria to give. An edit in that window makes the rows the
      // recruiter's own, like any edit on a prefill.
      aiCriteriaRef.current.clear();
      setCriteria((prev) => reconcileCriteria(prev, [""]));
      criteriaSeededFromRef.current = null;
      // `formHoldsOwnWorkRef` is NOT written here: a non-null
      // `criteriaSeededFromRef` already implies it is false (only a prefill
      // sets one, and a prefill clears the other). A write that can only ever
      // set `false → false` reads as a guard and is not one.
    }
    // What also resets is the BOOKKEEPING — not edited yet, not seeded yet, no
    // run started yet — so a review file selected next WILL prefill and, per
    // the rule in the seeding effect below, will replace whatever is in the
    // form. Missing this reset turned the prefill into a one-shot-per-session
    // feature (iter 2 — Agent C C2-a / Agent A blocker 3).
    criteriaDirtyRef.current = false;
    seededImportIdRef.current = null;
    runStartedForSelectionRef.current = false;
    // `formSettledFor` (declared with the prefill state below): a raw list's
    // criteria are settled now; a review file's when the seeding effect answers
    // (it runs after this one, so its answer wins within the same flush).
    setFormSettledFor(selected?.import_format === "ai_review" ? null : selectedImportId);
  }, [selectedImportId]);

  /**
   * Prefill the criteria when the selected list is itself an AI Review.
   *
   * This is the other half of Trello #711 cl.5's single "AI Review" CTA: on a
   * Raw Import it opens this page empty, on a review file it opens it with that
   * review's criteria already in the form. A recruiter then edits and runs,
   * which is the card's "Edit criteria and rerun → prefilled creator, creates a
   * NEW AI Review instance" — new because that is the only thing
   * `POST /ai-reviews` can do.
   *
   * ONE-SHOT, keyed on the import id. `useLatestAiReview` is a live query: it
   * refetches on focus and on remount, and a seed that ran on every resolve
   * would wipe edits the recruiter had made in between. `seededImportIdRef` is
   * the same shape `ImportSelectionBar`'s `adoptedRef` uses for deep-link
   * adoption, rather than a second idiom for the same job.
   *
   * `setCriteria` DIRECTLY, not `handleCriteriaChange`: that handler marks an
   * existing run stale, and seeding is not an edit — routing through it would
   * flag a run the recruiter has not touched.
   *
   * `description` first, `name` as the fallback: this page writes the typed
   * sentence into both, so either round-trips for a run it made; another client
   * may have set only `name`, and a blank row would be worse than the short
   * form.
   */
  const seededImportIdRef = useRef<string | null>(null);
  /**
   * The selection whose criteria are SETTLED: a raw list once the selection
   * effect has cleared the previous file's untouched prefill, a review file
   * once the seeding effect has answered (seeded, refused to overwrite the
   * recruiter's work, or found nothing to give). Reset on every selection
   * change, so going back to a list never reads an old answer.
   *
   * State, not a ref: variant B of "Suggest from Thrive" waits on it, and a
   * state set beside `setCriteria` lands in the same render as the rows, so the
   * ask reads the criteria the form will show. A ref would be read by the
   * child's effect, which runs BEFORE this page's effects.
   */
  // Seeded for the selection restored on mount: the selection effect skips the
  // first render (`lastImportIdRef` starts at it), so it would never say so.
  const [formSettledFor, setFormSettledFor] = useState<string | null>(() =>
    selected?.import_format === "ai_review" ? null : selectedImportId
  );
  /** Has the recruiter edited criteria since the CURRENT import was selected?
   *  Reset by the selection effect, set by `handleCriteriaChange`. */
  const criteriaDirtyRef = useRef(false);
  /** Has a run been STARTED for the current selection?
   *
   *  A ref, not `reviewId`, and that is the whole point. Both live in the same
   *  render: the selection effect calls `setReviewId(null)` and the seeding
   *  effect runs in the SAME flush, where `reviewId` is still the previous
   *  selection's job id. Reading it there made `pristine` false on every
   *  switch, marked the import permanently seeded, and killed the prefill —
   *  the iter-2 fix reintroducing the iter-1 bug by a different door
   *  (`/pr-review` iter 3, Agent A blocker 1). A ref is written and read
   *  synchronously, so it says what has actually happened. */
  const runStartedForSelectionRef = useRef(false);
  /** Which import's criteria are sitting in the form UNTOUCHED — `null` once the
   *  form holds the recruiter's own typing (or nothing).
   *
   *  NOT a duplicate of `seededImportIdRef`, and the two must not be folded
   *  together: that one is RESET by the selection effect (it has to be, or the
   *  prefill is one-shot-per-session — iter 2), so it describes the CURRENT
   *  SELECTION. This one describes the CONTENT of the form, which is precisely
   *  the thing that outlives a selection change. Different lifetimes. */
  const criteriaSeededFromRef = useRef<string | null>(null);
  /** Does the form hold work that is the RECRUITER'S — typed, edited, or
   *  carried — rather than an untouched prefill or nothing at all?
   *
   *  A ref rather than a read of `criteria`, and that distinction cost a round:
   *  the selection effect CLEARS an untouched prefill and the seeding effect
   *  runs in the same flush, where `criteria` still holds the rows that are
   *  about to disappear. Reading state there made every switch between review
   *  files look like "the recruiter typed something", which killed the prefill
   *  outright. This is written at every point that writes the criteria, so it
   *  is never one render behind. */
  const formHoldsOwnWorkRef = useRef(false);
  /**
   * `?criteriaFrom=<id>` — carry that review's criteria into this form and pin
   * them (PRD `2026-09-10-ai-review-criteria-reuse`, FR3).
   *
   * A SECOND instance of `useLatestAiReview`, which is safe for the reason its
   * own docblock gives: the query key carries the import id and the hook is
   * forbidden `placeholderData`, so the two instances cannot serve each other's
   * criteria across a key change.
   *
   * Declared BEFORE the seeding effect below so its `formHoldsOwnWorkRef` write
   * is visible to it in the same flush — the ordering lesson
   * `runStartedForSelectionRef` paid for.
   */
  const carriedReview = useLatestAiReview({
    client: activeAiReviewClient,
    importId: criteriaFromId,
    enabled: criteriaFromId !== null,
    // NO RETRIES, unlike the prefill. The two failures are not equivalent: a
    // prefill that blips is invisible, while this one holds the criteria back
    // AND suppresses the prefill until it settles. A malformed id answers 422
    // and, with the default three attempts, left the page saying nothing at all
    // for over twenty seconds (measured live, 2026-09-11). A bad id does not
    // become a good one on the second ask.
    retry: false,
  });
  /** One-shot per id — the shape `ImportSelectionBar`'s `adoptedRef` uses. */
  const carriedAdoptedRef = useRef<string | null>(null);
  /** The source could not be resolved: a legacy row, or a build that failed. */
  const [carryFailed, setCarryFailed] = useState(false);
  // `criteria` IS a dependency and is listed — the merge appends onto whatever
  // the form holds when the source resolves, so a re-run on an edit made during
  // that window is correct, and the one-shot ref makes every later re-run a
  // no-op.
  useEffect(() => {
    if (!criteriaFromId) return;
    if (carriedAdoptedRef.current === criteriaFromId) return;

    // ONE normalisation, used for both the emptiness test and the text that
    // lands in the form.
    //
    // `description || name` alone is wrong on a WHITESPACE description: `"   "`
    // is truthy, so it wins the `||` and the usable `name` beside it is
    // discarded — while an EMPTY description falls through to that same name
    // and is kept. Two criteria differing by one space took opposite paths
    // (PR #869, iter 3, Agent C). Trimming each side before the `||` is what
    // makes them agree.
    //
    // Filtering at all is iter 2's fix: a run whose criteria are all blank
    // would otherwise pin a banner over an empty form, promising to keep
    // criteria that are not there. To a recruiter that is indistinguishable
    // from no source, so it takes the same arm as a 404.
    const incoming = carriedReview.data?.criteria
      ?.map((c) => c.description?.trim() || c.name?.trim() || "")
      .filter((text) => text !== "");
    // Nothing to carry, and we now KNOW it — a 404 (the repository constrains
    // `by-import` to `applied`, so a legacy or failed build has no row) or a
    // resolved run with an empty `criteria_config`. Decided once, so a refetch
    // cannot reopen the question.
    if (carriedReview.isError || (carriedReview.isSuccess && !incoming?.length)) {
      carriedAdoptedRef.current = criteriaFromId;
      setCarryFailed(true);
      setCarryPending(false);
      return;
    }
    // Still resolving. Deliberately NO decision recorded — same as the seeding
    // effect's 404 arm, a later success may still land.
    if (!incoming?.length) return;

    carriedAdoptedRef.current = criteriaFromId;
    // APPEND, capped, deduped. `criteria` is read here rather than through a
    // functional update because the merge's second output — how many the cap
    // refused — has to reach `setPinned`, and a state updater is not a place to
    // run effects from.
    const merged = mergeCarriedCriteria(
      criteria,
      // Already normalised above: `description` first, `name` as the fallback,
      // each trimmed BEFORE the choice. The selection's own prefill does NOT
      // apply that rule — it still reads `c.description || c.name` untrimmed
      // (see the seeding effect below), so a whitespace-only description loses
      // the usable name beside it. That line is pre-existing on `dev` and is
      // left alone here (PR #869, iter 4, Agent A NEW-12): it is a different
      // run, a different defect, and not this card's.
      incoming,
      MAX_CRITERIA,
      nextCriterionId
    );
    aiCriteriaRef.current.clear();
    setCriteria(merged.criteria);
    formHoldsOwnWorkRef.current = true;
    setPinned({ sourceImportId: criteriaFromId, dropped: merged.dropped });
    setCarryPending(false);
  }, [
    criteriaFromId,
    carriedReview.data,
    carriedReview.isError,
    carriedReview.isSuccess,
    criteria,
  ]);

  const isReviewFileSelected = selected?.import_format === "ai_review";
  /**
   * The criteria belong to the SESSION, not to the selected list — so the
   * selection's own prefill must not run at all.
   *
   * TWO terms, because one alone leaves a hole at each end.
   *
   * `carryPending` covers the window before the criteria land: arriving on
   * `?criteriaFrom=A` with review file B ALREADY selected, B's criteria resolve
   * first (it is the selection, fetched on mount) while A's are still in
   * flight. The seed would land, and then A's arrivals would be APPENDED to
   * B's — a rubric the recruiter never asked for, from two files, with nothing
   * on screen saying so. It is `true` from the first render because the param
   * is known synchronously, which closes the race rather than narrowing it —
   * but ONLY through the guard in the seeding effect. The `enabled` flag alone
   * cannot: it stops the request, and a warm cache needs no request (PR #869,
   * iter 2, Agents A and C, who found that independently).
   *
   * `pinned` covers every render after that.
   *
   * Both go false when the recruiter presses `Stop keeping`, and that is the
   * point of not testing the URL directly: the param stays in the address bar
   * (this page never writes the query string), so a `criteriaFromId !== null`
   * test would leave the prefill suppressed for the rest of the session and
   * make the button a label with no behaviour behind it.
   */
  const criteriaAreSessionOwned = carryPending || pinned !== null;
  const sourceReview = useLatestAiReview({
    client: activeAiReviewClient,
    importId: selectedImportId,
    // Saves the REQUEST while the criteria are the session's. It does NOT save
    // the seed — the guard in the effect below does, because a warm cache needs
    // no request (PR #869, iter 3, Agent C: this comment survived the round
    // that proved it wrong and went on claiming the effect below needed no
    // guard of its own, while that very guard sat a few dozen lines under it.
    // No line count here on purpose — the last one was wrong by the time it
    // was read, iter 4, Agent C M9).
    enabled: isReviewFileSelected && !criteriaAreSessionOwned,
    // A 404 is already an answer (`null`, no retry); another error is not
    // retried either, so rows held from the previous file are not shown
    // for a retry's backoff before collapsing.
    retry: false,
  });
  useEffect(() => {
    if (!selectedImportId || !isReviewFileSelected) return;
    if (seededImportIdRef.current === selectedImportId) return;

    // THE SESSION-OWNED GUARD, and its history is the reason it reads the way
    // it does. Written 2026-09-11 WITH a stamp; the stamp made a temporary
    // condition permanent (PR #869, iter 1, Agent C): a carry still in flight
    // marked the selection "decided", and when that carry failed nothing
    // unwound it, so the selected file's own prefill was suppressed for the
    // session. I then deleted the guard entirely — and that reopened the race
    // it was the only thing covering (iter 2, Agent C).
    //
    // WHY THE QUERY GATE IS NOT ENOUGH, stated plainly because I got it wrong:
    // `enabled: false` stops the FETCH, not the READ. With a WARM cache — the
    // recruiter was just using AI Review on file B — `sourceReview.data` is
    // there on the FIRST render, so the seed fires before the carry resolves
    // and the carry then appends to it. Two files' criteria in one form, with
    // "This list's own criteria were not loaded" printed above them.
    //
    // So: guard, and NO STAMP. The condition is temporary by nature — it ends
    // when the carry lands or fails — and a stamp is how a temporary condition
    // becomes a permanent one.
    if (criteriaAreSessionOwned) return;

    // Seed ONLY into a form untouched FOR THIS SELECTION, and decide once.
    //
    // Two rounds of review shaped this, in opposite directions:
    //
    //  * iter 1 (Agent C, I2): the ref alone let the seed fire on the FIRST
    //    resolve — a network round trip — so typing during that window was
    //    overwritten, and because the swap bypassed `handleCriteriaChange` an
    //    already-previewed run was not marked stale, leaving Apply enabled over
    //    criteria nobody scored.
    //  * iter 2 (Agent C C2-a, Agent A blocker 3): the guard I added for that
    //    was SESSION-scoped — it inspected whether `criteria` looked untouched,
    //    and `criteria` is deliberately not reset when the selection changes. So
    //    switching from one review file to another left the FIRST file's
    //    criteria in the form and refused to seed the second. The headline
    //    feature, silently off after the first use.
    //
    // `criteriaDirtyRef` is the correction: it tracks edits SINCE THIS IMPORT
    // WAS SELECTED (reset by the selection effect), so "untouched" means
    // untouched for this file rather than for this session.
    //
    // THE RULE — REVERSED on 2026-09-11 (Lucas), and the old one is written out
    // below because this file has twice grown comments asserting the opposite of
    // its own code.
    //
    // WAS: criteria typed against import A ARE replaced when the recruiter
    // selects review file B — "they changed what they are reviewing, and B's own
    // criteria are what the prefill exists to show".
    //
    // IS: **the recruiter's own criteria are never overwritten.** Typing three
    // sentences and then picking a review file used to erase all three with no
    // warning (reproduced live on app.local before the change). Nothing the
    // prefill can offer is worth silently deleting work that was typed by hand.
    //
    // What is still replaced is an UNTOUCHED PREFILL: switching from review file
    // A to review file B without having touched A's criteria still shows B's.
    // Nothing of the recruiter's is lost there, and it is what makes the
    // prefill useful when browsing between review files.
    //
    // NOTHING IS SAID when the prefill is declined (product decision, same day,
    // taken with the counter-argument on the table: a review file whose criteria
    // never appear can read as broken). This used to be qualified — the pinned
    // banner carried its own "This list's own criteria were not loaded" line —
    // but the banner was removed on 2026-09-14 (Lucas, seeing it on
    // `app.local`), so the rule is now unqualified: the page says nothing at
    // all about a declined prefill.
    //
    // ONE-WAY FOR THE CURRENT SELECTION, and this is the honest reading of
    // "emptying the form hands it back" (PR #869, Agent C): the decision is
    // stamped on `seededImportIdRef`, so clearing the rows does NOT make THIS
    // list's criteria appear. The next selection prefills normally. Making it
    // two-way would mean re-running this effect on every keystroke, which is
    // how a prefill lands in the middle of a rewrite.
    //
    // The way OUT of a review file is the selection effect's half of the same
    // rule: an untouched prefill is cleared there. So B never inherits A's
    // criteria — not while B's own run is still resolving, and not at all when
    // B's run cannot be resolved (404, or a build that failed), which is the
    // case this effect can only answer by returning below.
    //
    // Marking the ref before returning is the important half — it records "we
    // decided not to seed this import", so a later refetch cannot come back and
    // clobber the edits we just protected.
    const pristine = !criteriaDirtyRef.current && !runStartedForSelectionRef.current;
    if (!pristine) {
      seededImportIdRef.current = selectedImportId;
      setFormSettledFor(selectedImportId);
      return;
    }

    // The recruiter's OWN criteria, kept across the selection change.
    //
    // `criteriaDirtyRef` above cannot carry this: the selection effect resets it
    // on every change, so work typed against the PREVIOUS selection reads as
    // pristine here. What survives a selection change is the FORM, which is what
    // `formHoldsOwnWorkRef` describes.
    if (formHoldsOwnWorkRef.current) {
      seededImportIdRef.current = selectedImportId;
      setFormSettledFor(selectedImportId);
      return;
    }

    const criteriaFromRun = sourceReview.data?.criteria;
    // A review file whose run cannot be resolved (404: a legacy row, or a build
    // that failed) simply gets the empty form. Nothing to say about it — and no
    // decision recorded, so a later successful resolve may still seed.
    if (!criteriaFromRun?.length) {
      // Settled with nothing to give: rows still holding the PREVIOUS review
      // file's untouched prefill (kept by the selection effect until this one
      // answered) collapse to the usual one blank row. This line is past both
      // "the recruiter's own work" guards above.
      const heldFrom = criteriaSeededFromRef.current;
      if (
        (sourceReview.isSuccess || sourceReview.isError) &&
        heldFrom !== null &&
        heldFrom !== selectedImportId
      ) {
        criteriaSeededFromRef.current = null;
        aiCriteriaRef.current.clear();
        setCriteria((prev) => reconcileCriteria(prev, [""]));
      }
      if (sourceReview.isSuccess || sourceReview.isError) setFormSettledFor(selectedImportId);
      return;
    }
    seededImportIdRef.current = selectedImportId;
    criteriaSeededFromRef.current = selectedImportId;
    setFormSettledFor(selectedImportId);
    // Same as the clear above: reaching this line means the guard that returns
    // when `formHoldsOwnWorkRef` is true did not fire, so it is already false.
    // Into the rows already there, by position: no row is reminted, so none
    // remounts (PRD 2026-10-06, C1).
    aiCriteriaRef.current.clear();
    setCriteria((prev) =>
      reconcileCriteria(
        prev,
        criteriaFromRun.map((c) => c.description || c.name)
      )
    );
    // `criteriaAreSessionOwned` IS a dependency and is listed: when the carry
    // settles it flips false, and this effect has to run again for the
    // selection's own prefill to land. That is the C1 path — without the
    // re-run, a failed carry would still leave the form empty.
  }, [
    selectedImportId,
    isReviewFileSelected,
    sourceReview.data,
    sourceReview.isSuccess,
    sourceReview.isError,
    criteriaAreSessionOwned,
  ]);

  // There is no applied-redirect effect here any more, and its absence is
  // the shape of this page again.
  //
  // It existed to hold the recruiter here until the run reported `applied`
  // so the page could open the derived file. It does not have to wait: the
  // row is written in the create transaction and its id now rides the 202,
  // so `onSuccess` navigates straight there and the destination polls the
  // run itself. Restoring this effect would mean two navigations racing for
  // the same file.

  /**
   * Veto hook for `<ImportSelectionBar>`. Nothing to veto any more.
   *
   * It guarded one thing: a FULL run sitting `succeeded` with its scores held
   * only in this page's session, which switching imports would discard. No
   * path leaves a run in that state now — the scoring worker applies the run
   * itself, so a full run's scores are on their way to a file from the moment
   * it is created, whatever this page does next. A preview's scores were always
   * throwaway.
   *
   * Note this stayed true when the page stopped leaving on the 202 (cl.5). The
   * veto was never about the page being ABOUT to unmount; it was about scores
   * that existed nowhere else, and there are none.
   */
  function allowImportSelect(_item: ImportSummary, _commit: () => void): boolean {
    return true;
  }

  /** Report suggestions moved into the form or deleted from it. Fire and
   *  forget: a lost event costs a count, never the recruiter's edit. */
  function recordSuggestionEvent(event: "added" | "removed", texts: string[]) {
    if (!selectedImportId || texts.length === 0) return;
    const body = { import_id: selectedImportId, event, suggestion_texts: texts };
    Promise.resolve()
      .then(() => activeAiReviewClient.recordSuggestionEvent(body))
      .catch(() => {});
  }

  /** The inline section's Add: the carry's merge (append, skip duplicates,
   *  cap) applied as a USER edit, each new row remembered as AI-suggested. */
  function addSuggestedCriteria(texts: string[]) {
    const before = new Set(criteria.map((c) => c.text.trim()));
    const next = mergeCarriedCriteria(criteria, texts, MAX_CRITERIA, nextCriterionId).criteria;
    // The merge drops blank rows and re-lays the rest by position, so row ids
    // move: re-key the AI rows by their text (PR #1118 iter 7, A7-2). An AI
    // row the recruiter had emptied is dropped by the merge, i.e. removed.
    // Each text keeps a queue, one entry per AI row, so a duplicate typed row
    // never inherits an AI origin and none is lost silently (iter 8, C8-1).
    const aiByText = new Map<string, string[]>();
    const keep = (text: string, suggested: string) =>
      aiByText.set(text, [...(aiByText.get(text) ?? []), suggested]);
    const removed: string[] = [];
    for (const c of criteria) {
      const suggested = aiCriteriaRef.current.get(c.id);
      if (suggested === undefined) continue;
      if (c.text.trim() === "") removed.push(suggested);
      else keep(c.text.trim(), suggested);
    }
    const added = texts.filter((t) => !before.has(t.trim()));
    for (const t of added) keep(t.trim(), t.trim());
    aiCriteriaRef.current.clear();
    for (const c of next) {
      const suggested = aiByText.get(c.text.trim())?.shift();
      if (suggested !== undefined) aiCriteriaRef.current.set(c.id, suggested);
    }
    // An AI row the merge folded into a same-text row is gone from the form.
    for (const left of aiByText.values()) removed.push(...left);
    // Every id in the re-keyed map is in `next`, so the edit reports nothing
    // removed itself; the emptied AI rows are reported here.
    handleCriteriaChange(next);
    recordSuggestionEvent("removed", removed);
    recordSuggestionEvent(
      "added",
      added.filter((t) => next.some((c) => c.text.trim() === t.trim()))
    );
  }

  function handleCriteriaChange(next: Criterion[]) {
    const ids = new Set(next.map((c) => c.id));
    const removed: string[] = [];
    for (const [id, text] of aiCriteriaRef.current) {
      if (ids.has(id)) continue;
      aiCriteriaRef.current.delete(id);
      // Only a row the recruiter deleted counts; one a selection's prefill
      // already replaced is forgotten silently.
      if (criteria.some((c) => c.id === id)) removed.push(text);
    }
    recordSuggestionEvent("removed", removed);
    setCriteria(next);
    // Every USER edit marks the form dirty for this selection, so the prefill
    // cannot land on top of it. Seeding sets `setCriteria` directly and
    // deliberately does NOT come through here — it is not an edit, and routing
    // it through would also mark a run stale that the recruiter never touched.
    criteriaDirtyRef.current = true;
    // Whatever is in the form is now the recruiter's, whether they typed it
    // from scratch or edited a prefill. It stops being keyed to the file it
    // came from and therefore survives a selection change.
    criteriaSeededFromRef.current = null;
    // ...and a NEXT review file's prefill must not overwrite it (2026-09-11).
    // Emptying every row hands the form back: there is nothing left to protect,
    // and the recruiter who cleared it may well want the next file's criteria.
    const formHoldsWork = next.some((c) => c.text.trim() !== "");
    formHoldsOwnWorkRef.current = formHoldsWork;
    // `pinned` is the OTHER half of the same rule, and it has to be released
    // here now. `Stop keeping` was the only thing that cleared it, and it went
    // with the banner on 2026-09-14 — without this, arriving on
    // `?criteriaFrom=` would keep `criteriaAreSessionOwned` true for the rest
    // of the session, so a recruiter who emptied every row would never see a
    // review file's own criteria again. That contradicts the sentence directly
    // above, which is the product rule, not an implementation detail.
    if (!formHoldsWork) setPinned(null);
    // Only a run that has actually started needs the stale gate — editing
    // criteria before the first "Preview scores" is just Configuring
    // (PRD state diagram: Configuring -> Configuring, no fetch). There are
    // no weights anymore (all criteria are equal weight), so a stale edit
    // is purely a criteria-text change.
    // A preview still in its POST counts as started: with the inline preview
    // the scoring happens inside that POST, so `reviewId` is set only when it
    // returns, and an edit made while it is in flight would otherwise leave
    // the old criteria's scores looking current (PR #1036 iter 6, I5).
    if (reviewId !== null || createRun.isPending) {
      setStale(true);
    }
    // The pin is deliberately NOT dropped. A recruiter who tweaks one line
    // before running it elsewhere has not stopped wanting to run it elsewhere —
    // and unpinning here would hand the next selection change the right to
    // delete the criteria, silently, one keystroke after they were kept.
  }

  /** Unpin — and leave the rows alone.
   *
   *  NOT a "Clear". Emptying the form already exists, one ✕ per row; a banner
   *  button that deletes eight hand-written criteria because someone wanted the
   *  notice gone is a different feature, and a worse one. What changes is only
   *  the RULE: from here the criteria behave like anything else the recruiter
   *  typed. */

  // The wire contract's `name` + `description` are both filled from the
  // SAME free-text criterion box — R2 has no separate "name" field, so the
  // criterion text serves as both (trimmed + truncated to the backend's
  // 100-char limit; CriterionRow already caps input at 100, this
  // trim+slice is defense in depth against a future relaxation of that
  // cap, and keeps the duplicate-name check below and the submitted `name`
  // in lockstep — computed once here rather than twice).
  const submittableCriteria = criteria
    .map((c) => ({ ...c, text: c.text.trim().slice(0, MAX_CRITERION_LENGTH) }))
    .filter((c) => c.text.length > 0);
  const submittedNames = submittableCriteria.map((c) => c.text);
  const hasValidCriterion = submittableCriteria.length > 0;

  /**
   * Do the criteria on screen belong to the SELECTED people list?
   *
   * Only the failed-carry notice asks, and it asks about PROVENANCE — "these
   * are this people list's own" is a claim about where the text came from, not
   * about whether the form is empty. `hasValidCriterion` was standing in for it
   * and the two come apart the moment the recruiter types: on a raw import,
   * where there are no criteria to prefill at all, typing one flipped the
   * notice to "these are this people list's own" (PR #869, iter 4, Agent A
   * NEW-11). `carryFailed` is a latch with no reset, so that reading persisted.
   *
   * Reading a ref during render rests on TWO INDEPENDENT facts. They were
   * collapsed into one derivation at iter 8 and separated again at iter 9
   * (Agent C NEW-C2, restoring what Agent A's NEW-16 had asked for) — neither
   * follows from the other, and the first is the one that makes the read legal
   * at all:
   *
   *  1. **Nothing writes this ref during render**, so the component is pure
   *     and two renders of a commit agree. That is true because all three
   *     writes live in an effect or an event handler — the prefill in the
   *     seeding effect, the clear on selection change, the clear in
   *     `handleCriteriaChange` — and none is reachable from a render path.
   *     It does NOT follow from anything about scheduling. This is what
   *     StrictMode's double render tests.
   *  2. **The value matches the criteria being painted.** Here scheduling is
   *     the mechanism: *a ref write schedules no render*, so a new ref value
   *     can only become visible on a render that some `setState` in the same
   *     synchronous block schedules. Every one of the three writes sits in
   *     such a block, and each block also calls `setCriteria`. The property
   *     doing the work is React's **automatic batching**: the whole block's
   *     updates flush as one. It is NOT that `setCriteria` is alone there —
   *     the selection clear's block calls seven other setters above it, and
   *     `handleCriteriaChange` also calls `setStale` (iter 9, Agent A NEW-23).
   *
   *     Where the ref is written FIRST, the pairing is free. Where it is
   *     written after, it matters — and at exactly one site, which is not the
   *     same as the two where the order is merely reversed:
   *
   *       - the clear on selection change writes after its `setCriteria`, but
   *         its effect early-returns unless `selectedImportId` CHANGED, so the
   *         ref holds the old id against a new selection and the predicate
   *         reads false either way. The two cannot coincide: the ref names a
   *         file only while that file is selected, and leaving it clears the
   *         ref right here — so arriving AT a file never finds the ref already
   *         holding it (iter 8, Agent A);
   *       - `handleCriteriaChange` writes after its `setCriteria` with the
   *         selection UNCHANGED, so a stale ref compares equal and paints
   *         "these are this people list's own" over text just typed. Nothing
   *         re-renders after that clear, so it would not flicker — it would
   *         stick.
   *
   *     That second case is pinned by a test: the edit at the end of "a failed
   *     carry does not suppress the selected file's own prefill". Its raw-import
   *     twin covers the same flip with the ref already null.
   *
   *     The first case cannot be pinned, and that is a fact about React rather
   *     than an omission: a `flushSync` inside the selection effect is DECLINED
   *     from a lifecycle method, warning and all, so the suite stays green and
   *     the probe measures nothing. Recorded because a green there would
   *     otherwise read as coverage (iter 9, Agent A, who re-measured it).
   *
   * The CONVERSE does not hold, and that is fine for two reasons worth having
   * in writing. The carry's own success path writes `setCriteria(merged)`
   * without touching this ref, so the ref can outlive the criteria it names.
   * It cannot be READ in that state: (a) the same block sets `pinned`, and the
   * notice renders only under `carryFailed && !pinned`; (b) reaching it would
   * need `carryFailed` true on a carry that SUCCEEDED. `carriedAdoptedRef` is
   * one-shot PER ID, which alone would not forbid it — a failed A followed by
   * a successful B composes exactly that state (iter 7, Agent A NEW-19). What
   * forbids it is that there is only ever one carry per mount: nothing here
   * writes `?criteriaFrom=`, the page has no `setSearchParams`, and the only
   * writer of that param lives on another route and navigates here. So the id
   * is constant, the single carry either failed or succeeded, and never both.
   * (`handleStopKeeping` used to clear `pinned` without writing either, and
   * was argued unreachable here for the same reason (b). It went with the
   * banner on 2026-09-14; nothing clears `pinned` now except emptying the
   * form.)
   *
   * It stays a ref rather than becoming state because the selection effect
   * reads it synchronously, where state would be one render behind — the flush
   * that cost this branch its first cut of FR13.
   */
  // The null check is the load-bearing half: with NOTHING selected both sides
  // are null, and a bare `===` would credit an empty form's criteria to a
  // people list that is not on screen. Reachable without contrivance — the CTA
  // carries no `import=`, so a recruiter who has never picked a list arrives
  // here with none. Pinned by "does not claim NO list's criteria as its own"
  // (PR #869, iter 5, Agent C m3, who also showed it was the one arm the
  // suite did not hold down).
  const seededFromImportId = criteriaSeededFromRef.current;
  const criteriaHoldSelectionsOwnPrefill =
    seededFromImportId !== null && seededFromImportId === selectedImportId;
  // The rows still show the PREVIOUS review file's untouched prefill while this
  // one's criteria load: never runnable against this file.
  const rowsHoldAnotherFilesPrefill =
    seededFromImportId !== null && seededFromImportId !== selectedImportId;

  // I1 (reviewer fix round 1): a duplicate name collapses in the worker's
  // `expected_names` set (see nu_assist_shared.ai_review.prompt), silently
  // dropping one row's score from the result matrix. Block Preview
  // client-side (case-sensitive exact match on the trimmed+truncated
  // name, same key the server-side validator in
  // `app/web/v2/ai_reviews/schemas.py` rejects) as the fast-feedback half
  // of the defense-in-depth pair — the 422 from the server is the other.
  const hasDuplicateCriteria = new Set(submittedNames).size !== submittedNames.length;
  const importHasNoCandidates = selected?.totalContacts === 0;
  // R1.8: the ONE blocking output-name rule. A missing `.csv` is silently
  // accepted (the recruiter owns the name) and a duplicate only warns — an
  // empty name is the only value the run cannot be given.
  //
  // It gates APPLY, not Preview (packet 3.2, decision 1). Packet 3.1 folded it
  // into `previewDisabled` because that was the only lever `ScoresTable`
  // exposed, which had the side effect of blocking a preview until the file
  // was named — wrong: you should be able to look at scores before deciding
  // what to call the file you save them into. `ScoresTable` now takes its own
  // `applyDisabled`, a deliberate, authorised deviation from this PRD's
  // "`ScoresTable.tsx` | Mod | C7–C12 only. No structural change." line.
  const hasOutputFilename = outputFilename.trim().length > 0;
  const previewDisabled =
    !selectedImportId ||
    !hasValidCriterion ||
    importHasNoCandidates ||
    hasDuplicateCriteria ||
    rowsHoldAnotherFilesPrefill;
  const previewDisabledHint = !selectedImportId
    ? "Select a people list to review"
    : rowsHoldAnotherFilesPrefill
      ? "Loading this list's criteria"
      : importHasNoCandidates
        ? "This people list has no candidates to score"
        : !hasValidCriterion
          ? "Add at least one criterion"
          : hasDuplicateCriteria
            ? "Criteria must be unique"
            : undefined;
  // Everything a run needs, plus the one thing only a FILE needs.
  const applyDisabled = previewDisabled || !hasOutputFilename;
  // Only voiced when the shared gates are already satisfied — otherwise
  // `previewDisabledHint` is already saying the more useful thing, next to the
  // button the recruiter should press first.
  const applyDisabledHint =
    !previewDisabled && !hasOutputFilename ? "Enter a title for the people list" : undefined;

  // `sampleSize` set => a PREVIEW run scoring only that many random contacts
  // (the "Preview sample" button); omitted => a full run over every contact.
  function handlePreviewScores(sampleSize?: number, isFullRun = false) {
    if (previewDisabled || !selectedImportId) return;
    setCreateError(false);
    setStale(false);
    setLastRunWasPreview(sampleSize != null);
    runStartedForSelectionRef.current = true;
    createRun.mutate(
      {
        import_id: selectedImportId,
        criteria: submittableCriteria.map((c) => ({
          ...criterionOrigin(c, aiCriteriaRef.current),
          name: c.text,
          description: c.text,
          // EQUAL weight for every criterion — keeps the backend's
          // Σweight>0 guard happy while collapsing the weighted total to
          // a simple average (Σ(s×w)/Σw == simple average when all w are
          // equal).
          weight_raw: 1,
          must_have_text: null,
        })),
        sample_size: sampleSize ?? null,
        // The name rides CREATE, because that is where the derived `imports`
        // row is written: the file is on the imports list, named, before this
        // 202 lands. It is ignored server-side for a preview, which produces
        // no file.
        output_filename: trimmedOutputFilename || null,
      },
      {
        onSuccess: (accepted) => {
          setReviewId(accepted.job_id);
          setRunImportId(selectedImportId);
          if (!isFullRun) return;
          // Drop the cached list — the row was just created and the list is one
          // Back away, so a stale cache would show a list missing the file the
          // recruiter just made.
          void queryClient.invalidateQueries({ queryKey: ["imports", "list"] });
          void queryClient.invalidateQueries({ queryKey: ["imports", "owners"] });

          // GO NOW. The derived row is written in the same transaction as the
          // run, so the destination exists the moment this response lands —
          // there has never been anything to wait for (Lucas, 2026-08-26).
          //
          // `?run=` is what makes the destination able to explain itself. It
          // cannot ask `GET /ai-reviews/by-import/{id}` about its own run,
          // because `find_latest_review_id` requires `applied` and this run is
          // `pending` — so a page that only knew its import id would show a
          // generic "Parsing people list" spinner over a Bedrock scoring run,
          // and nothing at all if the run failed. The creator holds both ids
          // here; handing the run id over costs nothing.
          //
          // FEATURE-FLAG COUPLING, restored here because the effect that used
          // to document it is gone and the exposure got worse, not better
          // (`/pr-review` iter 6, Agent C I3).
          //
          // The destination `/research/imports/:importId` is registered under
          // `my-imports` (`routing/routes.tsx`, `SUB_RESOURCE_ROUTES`) while
          // THIS page is gated on `ai-review`, and `FeatureRoute` bounces an
          // unreleased page to `/dashboard`. `my-imports` has already moved
          // three times.
          //
          // The old redirect fired at `applied`, after the recruiter had seen
          // scores. This one fires at the 202, before they have seen anything,
          // and `replace: true` destroys the creator's history entry. If
          // `my-imports` is ever off, a recruiter clicks "Review candidates",
          // pays for a Bedrock run, and lands on `/dashboard` with no run id,
          // no way Back, and their typed criteria gone with the unmounted
          // component. If that flag moves again, this navigate moves with it.
          //
          // Null here means one of TWO things, and this file now tells the
          // same story in all three places it mentions this field (round 9,
          // Agent A §3.5 — the earlier version of this line said "only for a
          // preview", contradicting the two longer notes above).
          //
          //  1. a PREVIEW, which returns above and never reaches here;
          //  2. a client running against an API that PREDATES the field, since
          //     it is optional on the wire. That is the deploy-skew window the
          //     abandon banner's full-run arm and the legacy confirmation block
          //     both exist for — and this early return is what keeps the page
          //     mounted so they can render.
          if (accepted.output_import_id == null) return;
          navigate(`/research/imports/${accepted.output_import_id}?run=${accepted.job_id}`, {
            replace: true,
          });
        },
        onError: () => setCreateError(true),
      }
    );
  }

  function handleScoreSort(key: string) {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  // "Review candidates": score EVERY contact and save them to a NEW import.
  // Always through the confirm dialog — the re-apply shortcut that used to sit
  // here needed a `succeeded` full run in this page's state, and no path leaves
  // one there now.
  function handleApplyAll() {
    if (applyDisabled) return;
    // Straight to the confirm dialog. The two recovery branches that used to
    // sit here — re-POST /apply on a stalled build, and re-apply a `succeeded`
    // full run whose apply had failed — both needed a full run's id in this
    // page's state, and no path leaves one there now. Recovery moved to where
    // the run is: a build that never lands leaves the derived row `failed` on
    // the imports list, which is where the recruiter is looking.
    setConfirmApplyOpen(true);
  }

  function handleConfirmApplyAll() {
    setConfirmApplyOpen(false);
    // The APPLY gate, not the preview one: this path ends in a file, so it
    // needs a name for it (and the name may have been cleared while the
    // confirm dialog was open).
    if (applyDisabled || !selectedImportId) return;
    // Full run (no sample_size), and we leave immediately. The scoring worker
    // hands the finished run to the apply worker itself; the browser has no
    // part in the second hop any more, which is what lets this page go.
    handlePreviewScores(undefined, true);
  }

  return (
    <div className="flex flex-col gap-6 p-8" data-testid="ai-review-page">
      <PageHeading
        title="AI Review"
        description="Score a people list's candidates against recruiter criteria (1 = definitely no · 2 = maybe · 3 = definitely yes)."
      />

      {/* The app-wide import selection. Collapsed to a one-line summary once
          an import is chosen (here or on Backchannel Tree); the finder only
          takes the space when there is nothing selected or the recruiter
          clicks Change. */}
      <ImportSelectionBar
        client={activeImportsClient}
        meEmail={meEmail}
        // ONE predicate, two renderings (dev #662): the finder disables an
        // unusable file in place and explains why, and the same sentence covers
        // a SHARED selection that is not usable here instead of silently
        // ignoring it.
        //
        // This comment used to open "D5 — no chaining" and cite a review file
        // as the thing being refused. D5 was REVERSED (Trello #711 cl.5): a
        // review file is now a perfectly good target and `aiReviewUnusable` no
        // longer rejects one. What survives is the object-existence rule — a
        // file whose own object was never written has no bytes to score.
        //
        // The requirement half is the one that matters, and it is not
        // symmetric with the filter: the selection is app-wide, so a recruiter
        // can arrive here with something chosen on another page. Without this,
        // the finder never opens, the page looks ready, and the refusal only
        // arrives as a 409 after they configure criteria and click. With it,
        // the bar expands the finder and says why.
        unusable={aiReviewUnusable}
        onBeforeSelect={allowImportSelect}
        // Arriving with criteria in hand AND NO LIST, the list is the open
        // question — so the finder starts open. Mount-only, and the bar still
        // owns every collapse.
        //
        // The `import` half is what keeps the single rerun button from opening
        // a picker nobody asked for: it sends `?import=<id>&criteriaFrom=<id>`,
        // so the list is already answered and the finder would be noise on the
        // most common arrival.
        //
        // A pure `?criteriaFrom=` still opens it — but be accurate about what
        // that is now. NOTHING IN THE PRODUCT EMITS ONE: the single rerun door
        // always sends both. The pure form is a typed or bookmarked URL, and
        // this gate is what keeps it working.
        //
        // The bar reconciles the DEEP-LINK param only — `import`/`importId`,
        // per `DEFAULT_DEEP_LINK_PARAMS`. `criteriaFrom` survives a collapse
        // merely because `new URLSearchParams(prev)` copies it, and picking a
        // list does NOT turn a bare carry into `?import=B&criteriaFrom=A`:
        // `writeCollapsedUrl` never adds an import param to a clean URL, and
        // `liveParamName` is null there even on the path that still navigates
        // to drop a live narrowing.
        //
        // The gate is mount-only: it answers the arrival and nothing after it.
        openFinderOnMount={criteriaFromId !== null && importParamId === null}
      />

      {/* Grid seam: this wrapper becomes a 2-column grid
          (`grid-cols-[1fr_320px]` or similar) once the AI Review Library
          right-hand panel (v38 lines 1793-1812) ships — deferred per the
          sub-task 04 PRD's "Library seam decision". Single column only for
          the POC; the criteria/results below are unaffected by that later,
          purely-additive change. */}
      <div className="grid grid-cols-1 gap-6">
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3" aria-label="People list title">
            <h2 className="text-h2 text-text">People list title</h2>
            <OutputFileNameField
              value={outputFilename}
              onChange={(next) => {
                setOutputFilename(next);
                setOutputFilenameEdited(true);
              }}
              duplicate={outputFilenameDuplicate}
            />
          </section>

          <section className="flex flex-col gap-3" aria-label="Criteria">
            {/* No action beside this heading, deliberately (Lucas, 2026-09-10).
                An in-page "Run these on another list…" button shipped here and
                was removed the same day: reuse has ONE door, the review file's
                own detail page, where the criteria the recruiter is looking at
                are the criteria they mean. A second door on this screen bought
                a second way to reach the same state and a control that spends
                header width being disabled most of the time.
                "Suggested by AI from Thrive" sits under the criteria
                (InlineCriteriaSuggestions), not beside this heading. */}
            <h2 className="text-h2 text-text">Criteria (configured per search)</h2>

            {carryFailed && !pinned ? (
              // biome-ignore lint/a11y/useSemanticElements: <output> is not a status line.
              <div
                role="status"
                data-testid="criteria-carry-failed"
                className="rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
              >
                {/* TWO endings over THREE states — the form is empty, holds
                    the selected list's own prefill, or holds what the
                    recruiter typed; the second ending covers the first and
                    third, and is true of both (PR #869, iter 5, Agent C m5:
                    an earlier version of this comment said the failure had
                    two outcomes, which stopped being the whole story once the
                    predicate became about provenance).

                    Which ending is asked of the provenance ref rather than of
                    "is the form non-empty" — see
                    `criteriaHoldSelectionsOwnPrefill`.

                    The notice itself STAYS. Clearing it when the selection's
                    own prefill landed was iter 2's fix for a notice sitting
                    over criteria that had just arrived, but it left the
                    recruiter who asked for A's criteria looking at B's with
                    nothing said at all — and on a warm cache it never painted
                    once (found at iter 3, confirmed closed at iter 4). */}
                {criteriaHoldSelectionsOwnPrefill
                  ? "Couldn't load the criteria from that review — these are this people list's own."
                  : "Couldn't load the criteria from that review. You can still write them here."}
              </div>
            ) : null}

            <CriteriaPanel
              criteria={criteria}
              onChange={handleCriteriaChange}
              maxCriteria={MAX_CRITERIA}
            />
            {inlineSuggestEnabled ? (
              <InlineCriteriaSuggestions
                client={activeAiReviewClient}
                importId={selected?.importId ?? null}
                searchIdThrive={selected?.searchIdThrive ?? null}
                existing={criteria}
                max={MAX_CRITERIA}
                // Ask once the form's criteria are settled for THIS selection:
                // no carry in flight, and (unless the session owns them) the
                // previous file's prefill cleared or this file's own decided.
                waitForCriteria={
                  carryPending || (!criteriaAreSessionOwned && formSettledFor !== selectedImportId)
                }
                onAdd={addSuggestedCriteria}
              />
            ) : null}
          </section>

          {createError ? (
            <div
              data-testid="create-run-error"
              className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
            >
              Couldn't start the AI review. Please try again.
            </div>
          ) : null}

          {/* The way out of a wait that will not end (`/pr-review` iter 1, Agent
              C — Critical).

              A run CAN sit non-terminal forever: the apply hand-off failing
              parks it back on `succeeded` on purpose, so an operator can
              recover scoring the recruiter already paid for. Nothing settles it
              — the sweeper settles the derived FILE, not the run, on a
              three-hour cadence. For the PREVIEW this page still watches, that
              would be a "Saving…" spinner until the tab is closed. A full run
              leaves on the 202 and `ImportDetailPage` holds the same deadline
              for it there.

              The banner sends them to the FILE, because the file is the thing
              that does settle: it is already on People Lists, and the sweeper
              flips it to failed if the build never lands. No retry button — the
              browser-driven apply is gone and recovery is operator-only, so a
              button here would promise something this page cannot do. */}
          {abandoned ? (
            <div
              data-testid="run-wait-abandoned"
              className="rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
            >
              {/* "so we've stopped waiting" is GONE from both arms, and that
                  is the fix here. Since iter 3 the deadline BACKS THE POLL OFF
                  to 30s rather than stopping it, so the page IS still watching
                  and a slow-but-healthy run still lands — the copy had been
                  telling the recruiter the opposite ever since
                  (`/pr-review` iter 5 M1, still open at iter 6). */}
              {abandonedIsPreview ? (
                <>
                  {pollFailed
                    ? "We lost contact with the server while this sample was being scored. "
                    : "Scoring this sample is taking longer than expected. We are still watching. "}
                  Nothing was saved either way — a preview never creates a people list. You can run
                  "Preview sample" again.
                </>
              ) : (
                <>
                  {pollFailed
                    ? "We lost contact with the server while this review was running. "
                    : "This review is taking longer than expected. We are still watching. "}
                  Your people list is on{" "}
                  {/* Same feature-flag coupling the 202 navigate documents:
                      this path is registered under `linkedin`/`my-imports`
                      while this page is gated on `ai-review`. If `my-imports`
                      moves again, this link needs to move with it. */}
                  <Link to="/research/imports" className="underline hover:no-underline">
                    People Lists
                  </Link>{" "}
                  — it will appear there when the run finishes, or as failed if it could not be
                  built.
                </>
              )}
            </div>
          ) : null}

          <ScoresTable
            run={run}
            isPolling={isPolling}
            stale={stale}
            totalCandidates={selected ? selected.totalContacts : null}
            onPreview={() => handlePreviewScores(SAMPLE_SIZE)}
            sampleSize={SAMPLE_SIZE}
            previewDisabled={previewDisabled}
            previewDisabledHint={previewDisabled ? previewDisabledHint : undefined}
            rows={previewRows}
            columns={previewColumns}
            rationaleFor={previewRationaleFor}
            resolvedAvatars={previewAvatars}
            onOpenPerson={personPanelEnabled ? openPerson : undefined}
            openPersonHandle={openHandle}
            openPersonRowId={openRowId}
            importId={selectedImportId}
            // A Company Map parent is a supported AI Review input
            // (`aiReviewUnusable` gates only on `status`), and its match-reason
            // columns come back from the contacts endpoint like any other
            // field. Without this the preview showed Name + criteria + Total
            // and the derived file's detail page then gained three columns the
            // preview never did — the same divergence the card is about. The
            // `variant` docstring's "events that have not happened yet" covers
            // export/Thrive/enrichment; BCT match reason is not one of them.
            // Not for an applied run: those rows come from `scoreOnlyRows`,
            // which carries no `bct_match_reason`, so the three Company Map
            // columns would render three dashes per row.
            isBct={selected?.import_format === "bct" && run?.status !== "applied"}
            scoredCount={scoredContactCount(run)}
            // `isLoading` (= `isPending && isFetching`), NOT `isPending`. In
            // React Query v5 a DISABLED query stays `pending` forever, and this
            // one is disabled for an applied run, an unselected import and a
            // run with no scored ids. Every one of those is masked today by the
            // `rows.length === 0` co-guard, but that is a coincidence of three
            // unrelated conditions — change any `enabled` term and the body
            // sticks on "Loading the scored candidates…" permanently.
            // `|| isFetching` so "Try again" has visible effect: a refetch of
            // an errored query keeps `status: "error"`, so `isLoading` stays
            // false and the body would sit on `ContactsFailed` unchanged until
            // the request settled — inviting a second impatient click.
            contactsPending={previewContactsQuery.isLoading || previewContactsQuery.isFetching}
            contactsFailed={previewContactsQuery.isError}
            onRetryContacts={() => void previewContactsQuery.refetch()}
            onApplyAll={handleApplyAll}
            applyDisabled={applyDisabled}
            applyDisabledHint={applyDisabledHint}
            sort={sort}
            onSort={handleScoreSort}
            // Two sources, and the FIRST one is the load-bearing half now.
            //
            // `createRun.isPending` covers the window between the click and the
            // 202. That window is not short: the endpoint opens a transaction,
            // counts the parent's contacts, writes two rows, commits, and sends
            // to SQS. For that whole time the confirm dialog has closed and,
            // without this, nothing on the page changed — on the one action
            // that spends real Bedrock money. The recruiter clicks again, and
            // a second full run is issued. (Data integrity survives: the
            // partial unique index refuses the second child and the loser is
            // handed the winner's run with no second SQS message. The UX does
            // not.) On `origin/dev` this was covered incidentally, by the
            // `autoApplyPending` flag the compound path set synchronously —
            // deleting that flag deleted the feedback with it.
            //
            // `run?.status === "applying"` is DEFENSIVE and unreachable from
            // this page, and saying so is the point. A full run leaves on the
            // 202 before it could reach `applying`; a preview is terminal at
            // `succeeded` and never enters the apply hop at all. The term costs
            // nothing and states the intent — "a run mid-apply is still
            // working" — but it is not claimed as covered
            // (`/pr-review` iter 6, Agent C M4).
            //
            // A PREVIEW's POST is not an apply: it shows the scoring state
            // (`previewPending`) instead, because the API may be scoring the
            // sample inside that request for up to ~50 s.
            applying={(createRun.isPending && !previewPosting) || run?.status === "applying"}
            previewPending={previewPosting}
            // The banner alone was not the fix (`/pr-review` iter 2, Agent C
            // C1-a). `ScoresTable` keeps its own in-flight predicate, so with
            // the banner up the primary button still read "Saving…" with a
            // spinner AND went enabled — offering a retry that does not exist
            // and would start a second paid run.
            abandoned={abandoned}
          />

          {/* DEFENSIVE, deploy-skew only — the same status as the abandon
              banner's full-run arm 640 lines above, and reconciled with it
              here because the two contradicted each other (round 8, Agent C
              I2).

              This block claimed to be "the one path that reaches `applied`
              with a null `output_import_id`", citing F6. To reach it the page
              must still be mounted at `applied`, and the only thing that keeps
              it mounted is the 202 navigation being skipped — which turns on
              the 202's `output_import_id`, not `AiReviewResult`'s. The
              correction at the top of this file establishes that the server
              cannot emit a full-run 202 with a null id (`create` mints
              `child_id` whenever `builds_a_file`; the double-click path returns
              the winner's), and a preview never reaches `applied` at all. So
              this is dead by that same argument, and citing F6 here repeated
              exactly the field conflation that correction exists to undo.

              It stays for the reason the other arm stays: `output_import_id` is
              OPTIONAL on the wire, so a bundle running against an API that
              predates the field would skip the navigation and land here. Not a
              live product path. */}
          {run?.status === "applied" ? (
            <div className="flex flex-col gap-1" data-testid="apply-success">
              <p className="text-body text-text">Review saved as a new people list</p>
              <p className="text-caption text-text-muted">
                Your original people list is unchanged.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Confirm before the compound "Review candidates" — it scores EVERY
          contact (real Bedrock cost + minutes) and then writes a new import. */}
      <ConfirmDialog
        open={confirmApplyOpen}
        title="Score all contacts and create the review?"
        // `displayTitle` on the NAME: this sentence names a people list, and
        // contact-list names never show their stored extension. The INPUT above
        // deliberately keeps it — there the value is editable and the extension
        // is functional, not decorative (`/pr-review` iter 4, Agent C I7).
        message={`This scores all ${selected?.totalContacts ?? 0} contacts with the AI and saves the results as a new people list, "${displayTitle(outputFilename)}". Your original people list is not changed. It runs the AI on every contact and may take a few minutes.`}
        confirmLabel="Score all contacts"
        onConfirm={handleConfirmApplyAll}
        onCancel={() => setConfirmApplyOpen(false)}
      />

      {openHandle && (
        <PersonPanel
          listTitle={panelListTitle}
          position={openIndex >= 0 ? { index: openIndex, total: personStops.length } : null}
          suspended={panelViewerOpen}
          onPrev={() => stepPerson(-1)}
          onNext={() => stepPerson(1)}
          onClose={closePerson}
          pagePath={
            personPagePath(openHandle) +
            (personUrl.tab === "profile" ? "" : `?tab=${personUrl.tab}`)
          }
          shareLinks={[
            {
              id: "list",
              label: "In this review",
              description: `Opens the AI Review of “${panelListTitle}” with this person open on this tab.`,
              url: personShareUrl(`${location.pathname}${location.search}`),
            },
            {
              id: "page",
              label: "Contact detail",
              description:
                "The person on their own, whatever list they came from. Keyed by the LinkedIn URL, so it works across lists and searches.",
              url: personShareUrl(
                `${personPagePath(openHandle)}${personUrl.tab === "profile" ? "" : `?tab=${personUrl.tab}`}`
              ),
            },
          ]}
        >
          {(nameId) => (
            <PersonView
              key={openHandle}
              client={peopleApi}
              handle={openHandle}
              aiReviewClient={activeAiReviewClient}
              onModalChange={setPanelViewerOpen}
              importId={selectedImportId}
              tab={personUrl.tab}
              onTabChange={personUrl.setTab}
              mode="list"
              row={openRow}
              avatarUrl={
                openRow?.profile_image_s3_key
                  ? previewAvatars.get(openRow.profile_image_s3_key)
                  : undefined
              }
              nameId={nameId}
            />
          )}
        </PersonPanel>
      )}
    </div>
  );
}
